import { parseAwsJson, unwrapApiPayload } from '@/utils/graphql'
import { buildGraphqlAuthInput, ON_CALL_AUTH_GRAPHQL_FIELDS } from '@/utils/onCallAuthGraphql'
import {
  buildDefaultOutgoingNotificationsForm,
  buildNotificationsForm,
  buildNotificationsInput,
  defaultNotificationsForm,
  normalizeOnCallNotifications,
  normalizeSendToList,
  usesArraySendTo,
} from '@/utils/onCallNotifications'
import {
  readChatbotDetailsFromOrg,
} from '@/utils/agentOtherSettings'
import {
  bodyFieldsFromJsonTree,
  bodyObjectFromFieldTree,
  collectLeafBodyFieldRows,
  isBodyFieldExampleRequired,
} from '@/utils/bodyFieldTree'

export { isLiteralBodyFieldValue, resolveBodyFieldExampleValue } from '@/utils/bodyFieldTree'

function pickString(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return ''
  for (const key of keys) {
    const value = raw[key]
    if (value != null && String(value).trim()) return String(value).trim()
  }
  return ''
}

function pickBool(raw, ...keys) {
  if (!raw || typeof raw !== 'object') return false
  for (const key of keys) {
    if (raw[key] != null) return Boolean(raw[key])
  }
  return false
}

export const ON_CALL_ACTION_TYPES = [
  {
    title: 'Call Redirect',
    value: 'CALL_REDIRECT',
    icon: 'mdi-phone-forward-outline',
    desc: 'Transfer call to another number',
  },
  {
    title: 'Fetch Response',
    value: 'FETCH_RESPONSE',
    icon: 'mdi-download-network-outline',
    desc: 'Call API and read response to caller',
  },
  {
    title: 'Post Response',
    value: 'POST_RESPONSE',
    icon: 'mdi-upload-network-outline',
    desc: 'Submit data and confirm to caller',
  },
  {
    title: 'Outgoing Call',
    value: 'OUTGOING_CALL',
    icon: 'mdi-call-made',
    desc: 'AI calls the customer and collects information',
  },
]

/** Per-type accent colors for list UI (icon + type chip share the same hue). */
export const ACTION_TYPE_ACCENTS = {
  CALL_REDIRECT: {
    slug: 'redirect',
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.12)',
    darkColor: '#34d399',
    darkBg: 'rgba(16, 185, 129, 0.16)',
  },
  FETCH_RESPONSE: {
    slug: 'fetch',
    color: '#2563eb',
    bg: 'rgba(37, 99, 235, 0.12)',
    darkColor: '#60a5fa',
    darkBg: 'rgba(37, 99, 235, 0.18)',
  },
  POST_RESPONSE: {
    slug: 'post',
    color: '#7c3aed',
    bg: 'rgba(124, 58, 237, 0.12)',
    darkColor: '#a78bfa',
    darkBg: 'rgba(124, 58, 237, 0.18)',
  },
  OUTGOING_CALL: {
    slug: 'outgoing',
    color: '#d97706',
    bg: 'rgba(245, 158, 11, 0.14)',
    darkColor: '#fbbf24',
    darkBg: 'rgba(245, 158, 11, 0.16)',
  },
}

export function actionTypeSlug(value) {
  return ACTION_TYPE_ACCENTS[value]?.slug || 'default'
}

export const ON_CALL_AUTH_TYPES = [
  { title: 'No Auth', value: 'none' },
  { title: 'API Key', value: 'api_key' },
  { title: 'Bearer', value: 'bearer' },
  { title: 'Basic Auth', value: 'basic' },
  { title: 'OAuth 2.0', value: 'oauth2_client' },
  { title: 'Custom Header', value: 'custom_header' },
  { title: 'HMAC', value: 'hmac' },
  { title: 'JWT', value: 'jwt' },
  { title: 'Query API Key', value: 'query_api_key' },
]

export const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE']

export function defaultAuthConfig() {
  return {
    type: 'none',
    key: '',
    value: '',
    token: '',
    username: '',
    password: '',
    token_url: '',
    client_id: '',
    client_secret: '',
    scope: '',
    headers: '{}',
    custom_headers: [],
  }
}

export function defaultConfig() {
  return {
    url: '',
    method: 'GET',
    headers: '{}',
    params: '{}',
    body: '{}',
    auth: defaultAuthConfig(),
    transfer_number: '',
    transfer_message: '',
    response_status_path: '',
    response_status_success_value: '',
    response_message_path: '',
    success_message: '',
    error_message: '',
    body_fields: [],
    sample_values: {},
    greeting: '',
    mission_prompt: '',
    required_field_rows: [],
    max_duration: 180,
    retry_attempts: 2,
    retry_interval: 300,
    webhook_url: '',
    webhook_method: 'POST',
    webhook_auth: defaultAuthConfig(),
    to_number: '',
  }
}

export function defaultOnCallForm() {
  return {
    action_name: '',
    action_description: '',
    action_type: 'FETCH_RESPONSE',
    ask_on_start: false,
    is_active: true,
    config: defaultConfig(),
    notifications: defaultNotificationsForm(),
    test_params: '{}',
  }
}

export function buildOutgoingCallGreetingFromOrg(org = {}) {
  const chatbot = readChatbotDetailsFromOrg(org)
  const agentName = chatbot.chatbot_name || 'our agent'
  const orgName = pickString(org, 'org_name', 'orgName') || 'our company'

  return `Hi {customer_name}, this is ${agentName} calling from ${orgName}. We have completed the {service_name} and would like your confirmation`
}

export function applyOutgoingCallOrgDefaults(form, org = {}, { onlyEmpty = true } = {}) {
  if (!form?.config) return form

  const config = { ...form.config }
  let notifications = form.notifications

  if (form.action_type !== 'OUTGOING_CALL') {
    return { ...form, config }
  }

  const greeting = buildOutgoingCallGreetingFromOrg(org)
  if (greeting && (!onlyEmpty || !String(config.greeting || '').trim())) {
    config.greeting = greeting
  }

  notifications = buildDefaultOutgoingNotificationsForm({
    actionName: form.action_name,
    requiredFieldRows: config.required_field_rows,
    org,
    existing: onlyEmpty ? form.notifications : null,
  })

  return {
    ...form,
    config,
    notifications,
  }
}

export function customHeadersFromAuth(auth) {
  if (!auth) return []

  const headers = normalizeAwsJsonMap(auth.headers)

  const entries = Object.entries(headers).filter(
    ([key]) => key && !String(key).startsWith('__'),
  )

  if (entries.length) {
    return entries.map(([key, value], index) => ({
      id: `ch-${index}`,
      key,
      value: String(value ?? ''),
    }))
  }

  if (auth.key) {
    return [{ id: 'ch-0', key: auth.key, value: auth.value || '' }]
  }

  return []
}

function fieldValueToFormString(value) {
  if (value == null) return ''
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

function parseBodyFieldValue(raw) {
  if (raw == null) return ''

  if (typeof raw === 'object') {
    return raw
  }

  const text = String(raw).trim()
  if (!text) return ''

  const first = text[0]
  if (first === '{' || first === '[') {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }

  if (first === '"' || first === "'" || /^(true|false|null)$/i.test(text)) {
    try {
      return JSON.parse(text)
    } catch {
      return text
    }
  }

  if (/^-?\d+(?:\.\d+)?$/.test(text)) {
    const numeric = Number(text)
    if (!Number.isNaN(numeric)) return numeric
  }

  return text
}

function bodyObjectFromFields(rows) {
  return bodyObjectFromFieldTree(rows)
}

export function customHeadersToObject(rows) {
  const headers = {}
  ;(rows || []).forEach((row) => {
    const key = String(row?.key ?? '').trim()
    if (!key) return
    headers[key] = String(row?.value ?? '')
  })
  return headers
}

function extractPlaceholderTokens(value = '') {
  return [...String(value).matchAll(/\{([a-zA-Z0-9_]+)\}/g)].map((match) => match[1])
}

function escapeRegex(value = '') {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function parseExampleIntoPlaceholders(valueTemplate, example, tokens = []) {
  if (!valueTemplate || !example || !tokens.length) return null

  const tokenOrder = []
  const literalParts = []
  let remaining = valueTemplate

  tokens.forEach((token) => {
    const marker = `{${token}}`
    const index = remaining.indexOf(marker)
    if (index === -1) return

    literalParts.push(remaining.slice(0, index))
    tokenOrder.push(token)
    remaining = remaining.slice(index + marker.length)
  })

  literalParts.push(remaining)

  if (!tokenOrder.length) return null

  let pattern = '^'
  literalParts.forEach((part, index) => {
    if (part) pattern += escapeRegex(part)
    if (index < tokenOrder.length) pattern += '(.+?)'
  })
  pattern += '$'

  try {
    const match = example.match(new RegExp(pattern))
    if (!match) return null

    const parsed = {}
    tokenOrder.forEach((token, index) => {
      parsed[token] = match[index + 1]
    })
    return parsed
  } catch {
    return null
  }
}

export function bodyFieldsFromJson(body, sampleValues = {}) {
  return bodyFieldsFromJsonTree(body, sampleValues)
}

function sampleValuesObjectFromFields(rows, existingSampleValues = {}) {
  const sample = { ...normalizeAwsJsonMap(existingSampleValues) }
  const leaves = collectLeafBodyFieldRows(rows)

  leaves.forEach((row) => {
    const key = String(row?.key ?? '').trim()
    const value = String(row?.value ?? '').trim()
    const example = String(row?.example_value ?? row?.exampleValue ?? '').trim()
    if (!key || !example) return

    const tokens = extractPlaceholderTokens(value)

    if (!tokens.length) {
      return
    }

    if (tokens.length === 1) {
      sample[tokens[0]] = example
      return
    }

    const parsed = parseExampleIntoPlaceholders(value, example, tokens)
    if (parsed) {
      Object.assign(sample, parsed)
    }
  })

  return sample
}

function enrichSampleValuesFromDescription(samples = {}, description = '') {
  const text = String(description || '')
  if (!text.trim()) return samples

  const enriched = { ...samples }
  Object.entries(BODY_FIELD_PLACEHOLDER_EXAMPLES).forEach(([key, defaultValue]) => {
    if (!text.includes(key) || enriched[key]) return
    enriched[key] = defaultValue
  })
  return enriched
}

export function buildSampleValuesFromForm(bodyFields = [], existingSampleValues = {}, actionDescription = '') {
  const merged = sampleValuesObjectFromFields(bodyFields, existingSampleValues)
  return enrichSampleValuesFromDescription(merged, actionDescription)
}

export function syncFormSampleValues(form) {
  if (!form?.config) return form

  form.config.sample_values = buildSampleValuesFromForm(
    form.config.body_fields,
    form.config.sample_values,
    form.action_description,
  )
  return form
}

const BODY_FIELD_PLACEHOLDER_EXAMPLES = {
  caller_number: '9876543210',
  caller_name: 'Ramu',
  caller_email: 'ramu@test.com',
  extracted_date: '2026-06-25',
  extracted_time: '10:00:00',
  extracted_topic: 'XcelAudit Discussion',
  org_id: 'org-12345',
}

const BODY_FIELD_KEY_EXAMPLES = {
  duration: '30',
  topic: 'XcelAudit Discussion',
  timezone: 'Asia/Kolkata',
  type: '2',
  phone_number: '9876842233',
  start_time: '2026-06-25T10:00:00',
  settings: '{"host_video":true,"waiting_room":true}',
}

export function examplePlaceholderForBodyField(key = '', value = '') {
  const fieldKey = String(key).trim().toLowerCase()
  const fieldValue = String(value).trim()

  const tokens = [...fieldValue.matchAll(/\{([a-zA-Z0-9_]+)\}/g)].map((match) => match[1])
  if (tokens.length === 1 && BODY_FIELD_PLACEHOLDER_EXAMPLES[tokens[0]]) {
    return BODY_FIELD_PLACEHOLDER_EXAMPLES[tokens[0]]
  }
  if (tokens.includes('extracted_date') && tokens.includes('extracted_time')) {
    return '2026-06-25T10:00:00'
  }

  if (fieldKey && BODY_FIELD_KEY_EXAMPLES[fieldKey]) {
    return BODY_FIELD_KEY_EXAMPLES[fieldKey]
  }

  if (fieldKey.includes('email')) return 'user@example.com'
  if (fieldKey.includes('topic')) return 'Meeting topic'
  if (fieldKey.includes('timezone')) return 'Asia/Kolkata'
  if (fieldKey.includes('date')) return '2026-06-25'
  if (fieldKey.includes('time')) return '10:00:00'
  if (fieldKey.includes('phone') || fieldKey.includes('number')) return '9876842233'
  if (fieldKey.includes('duration')) return '30'
  if (fieldKey.includes('setting')) return '{"host_video":true}'

  if (fieldValue && !fieldValue.includes('{')) {
    return fieldValue.length > 42 ? `${fieldValue.slice(0, 39)}...` : fieldValue
  }

  if (fieldKey) return `Sample for ${fieldKey}`
  return 'Example value'
}

export function validateBodyFieldExamples(bodyFields = []) {
  const leaves = collectLeafBodyFieldRows(bodyFields).filter(
    (row) => String(row?.key ?? '').trim(),
  )
  if (!leaves.length) return ''

  const missing = leaves.filter((row) => {
    const value = String(row?.value ?? '').trim()
    if (!value || !isBodyFieldExampleRequired(value)) return false
    return !String(row?.example_value ?? '').trim()
  })
  if (!missing.length) return ''

  const labels = missing.map((row) => row.key).join(', ')
  return `Add example values for test runs: ${labels}`
}

export function sampleValuesJsonFromFields(rows, existingSampleValues = {}, actionDescription = '') {
  const sample = buildSampleValuesFromForm(rows, existingSampleValues, actionDescription)
  return prepareAwsJsonInput(sample, { requireObject: true })
}

export function bodyJsonFromFields(rows) {
  const body = bodyObjectFromFields(rows)
  if (!Object.keys(body).length) return undefined
  try {
    return JSON.stringify(body)
  } catch {
    return undefined
  }
}

function normalizeAwsJsonMap(value) {
  const parsed = parseAwsJson(value)
  if (parsed == null || typeof parsed !== 'object' || Array.isArray(parsed)) {
    return {}
  }
  const clean = {}
  Object.entries(parsed).forEach(([key, val]) => {
    const normalizedKey = String(key).trim()
    if (!normalizedKey || normalizedKey.startsWith('__')) return
    if (val === undefined) return
    clean[normalizedKey] = val
  })
  return clean
}

/**
 * Prepare AWSJSON fields for AppSync GraphQL variables.
 * Returns a JSON string (AWSJSON scalar) or undefined when empty/invalid.
 */
function prepareAwsJsonInput(value, { requireObject = true } = {}) {
  if (value == null || value === '') return undefined

  let parsed = value
  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (!trimmed || trimmed === '{}' || trimmed === '[]') return undefined
    try {
      parsed = JSON.parse(trimmed)
    } catch {
      return undefined
    }
  } else if (typeof value === 'object') {
    if (value.__invalid) return undefined
    parsed = value
  }

  if (parsed == null) return undefined

  if (requireObject) {
    if (typeof parsed !== 'object' || Array.isArray(parsed)) return undefined
    const clean = {}
    Object.entries(parsed).forEach(([key, val]) => {
      const normalizedKey = String(key).trim()
      if (!normalizedKey || normalizedKey.startsWith('__')) return
      if (val === undefined) return
      clean[normalizedKey] = val
    })
    if (Object.keys(clean).length === 0) return undefined
    parsed = clean
  } else if (typeof parsed === 'object' && !Array.isArray(parsed)) {
    if (Object.keys(parsed).length === 0) return undefined
  }

  try {
    return JSON.stringify(parsed)
  } catch {
    return undefined
  }
}

function buildQueryApiKeyAuth(auth) {
  return buildGraphqlAuthInput({ ...auth, type: 'query_api_key' })
}

function buildJwtAuth(auth) {
  return buildGraphqlAuthInput({ ...auth, type: 'jwt' })
}

function buildHmacAuth(auth) {
  return buildGraphqlAuthInput({ ...auth, type: 'hmac' })
}

function buildCustomHeaderAuth(auth) {
  return buildGraphqlAuthInput({ ...auth, type: 'custom_header' })
}

export function parseJsonField(value, fallback = {}) {
  if (value == null || value === '') return { ...fallback }
  if (typeof value === 'object') {
    if (Array.isArray(value) || value.__invalid) return { ...fallback, __invalid: true }
    return { ...value }
  }
  try {
    const parsed = JSON.parse(String(value))
    return parsed ?? { ...fallback }
  } catch {
    return { ...fallback, __invalid: true }
  }
}

export function stringifyJsonField(value) {
  if (value == null || value === '') return '{}'
  if (typeof value === 'string') {
    try {
      JSON.parse(value)
      return value
    } catch {
      return value
    }
  }
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return '{}'
  }
}

export function isHttpActionType(actionType) {
  return actionType === 'FETCH_RESPONSE' || actionType === 'POST_RESPONSE'
}

export function isOutgoingCallActionType(actionType) {
  return actionType === 'OUTGOING_CALL'
}

const KNOWN_ACTION_TYPES = new Set([
  'CALL_REDIRECT',
  'FETCH_RESPONSE',
  'POST_RESPONSE',
  'OUTGOING_CALL',
])

export function normalizeActionTypeValue(value) {
  const raw = String(value ?? '').trim()
  if (!raw) return ''

  const upper = raw.toUpperCase().replace(/[\s-]+/g, '_')
  if (KNOWN_ACTION_TYPES.has(upper)) return upper

  if (upper === 'POST' || upper === 'POST_RESPONSE') return 'POST_RESPONSE'
  if (upper === 'FETCH' || upper === 'GET' || upper === 'FETCH_RESPONSE') return 'FETCH_RESPONSE'
  if (upper === 'REDIRECT' || upper === 'CALL_REDIRECT') return 'CALL_REDIRECT'
  if (upper === 'OUTGOING' || upper === 'OUTGOING_CALL') return 'OUTGOING_CALL'

  return upper
}

export function resolveActionType(raw, configRaw = {}) {
  const explicit = pickString(
    raw,
    'action_type',
    'actionType',
    'type',
    'Type',
    'ACTION_TYPE',
  )
  if (explicit) return normalizeActionTypeValue(explicit) || 'FETCH_RESPONSE'

  const fromConfig = pickString(configRaw, 'action_type', 'actionType')
  if (fromConfig) return normalizeActionTypeValue(fromConfig) || 'FETCH_RESPONSE'

  if (pickString(configRaw, 'transfer_number', 'transferNumber')) {
    return 'CALL_REDIRECT'
  }

  if (
    pickString(configRaw, 'greeting')
    || pickString(configRaw, 'mission_prompt', 'missionPrompt')
    || pickString(configRaw, 'to_number', 'toNumber')
    || (Array.isArray(configRaw.caller_list) && configRaw.caller_list.length)
  ) {
    return 'OUTGOING_CALL'
  }

  return 'FETCH_RESPONSE'
}

let requiredFieldRowId = 0

function nextRequiredFieldRowId() {
  requiredFieldRowId += 1
  return `rf-${requiredFieldRowId}`
}

export function requiredFieldsFromAwsJson(value) {
  let parsed = value
  if (value == null) {
    parsed = []
  } else if (typeof value === 'string') {
    parsed = parseAwsJson(value)
  }

  if (!Array.isArray(parsed)) return []

  return parsed
    .map((item, index) => {
      const field = pickString(item, 'field')
      const prompt = pickString(item, 'prompt')
      if (!field && !prompt) return null
      return {
        id: nextRequiredFieldRowId(),
        key: field,
        value: prompt,
        item_sequence: index + 1,
      }
    })
    .filter(Boolean)
}

export function requiredFieldsToAwsJson(rows = []) {
  const items = (rows || [])
    .map((row) => ({
      field: String(row?.key ?? row?.field ?? '').trim(),
      prompt: String(row?.value ?? row?.prompt ?? '').trim(),
    }))
    .filter((row) => row.field && row.prompt)

  if (!items.length) return undefined

  try {
    return JSON.stringify(items)
  } catch {
    return undefined
  }
}

function parsePositiveInt(value, fallback) {
  const parsed = Number.parseInt(String(value ?? '').trim(), 10)
  if (Number.isNaN(parsed) || parsed < 0) return fallback
  return parsed
}

function buildWebhookAuthInput(webhookAuth) {
  return buildGraphqlAuthInput(webhookAuth)
}

function authFieldsFromRecord(authRaw = {}) {
  const type = pickString(authRaw, 'type') || 'none'

  if (type === 'hmac') {
    return {
      type,
      key: pickString(authRaw, 'header', 'key'),
      value: pickString(authRaw, 'secret', 'value'),
      scope: pickString(authRaw, 'algorithm', 'scope') || 'sha256',
      token: pickString(authRaw, 'token'),
      username: pickString(authRaw, 'username'),
      password: pickString(authRaw, 'password'),
      token_url: pickString(authRaw, 'token_url', 'tokenUrl'),
      client_id: pickString(authRaw, 'client_id', 'clientId'),
      client_secret: pickString(authRaw, 'client_secret', 'clientSecret'),
      headers: normalizeAwsJsonMap(authRaw.headers),
    }
  }

  if (type === 'jwt') {
    return {
      type,
      token: pickString(authRaw, 'secret', 'token'),
      key: pickString(authRaw, 'key'),
      value: pickString(authRaw, 'value'),
      username: pickString(authRaw, 'username'),
      password: pickString(authRaw, 'password'),
      token_url: pickString(authRaw, 'token_url', 'tokenUrl'),
      client_id: pickString(authRaw, 'client_id', 'clientId'),
      client_secret: pickString(authRaw, 'client_secret', 'clientSecret'),
      scope: pickString(authRaw, 'scope'),
      headers: normalizeAwsJsonMap(authRaw.headers),
    }
  }

  if (type === 'query_api_key') {
    return {
      type,
      key: pickString(authRaw, 'param', 'key'),
      value: pickString(authRaw, 'key', 'value'),
      token: pickString(authRaw, 'token'),
      username: pickString(authRaw, 'username'),
      password: pickString(authRaw, 'password'),
      token_url: pickString(authRaw, 'token_url', 'tokenUrl'),
      client_id: pickString(authRaw, 'client_id', 'clientId'),
      client_secret: pickString(authRaw, 'client_secret', 'clientSecret'),
      scope: pickString(authRaw, 'scope'),
      headers: normalizeAwsJsonMap(authRaw.headers),
    }
  }

  if (type === 'custom_header') {
    const headers = normalizeAwsJsonMap(authRaw.headers)
    const entries = Object.entries(headers).filter(([key]) => key)
    if (entries.length) {
      return {
        type,
        key: entries[0][0],
        value: String(entries[0][1] ?? ''),
        token: pickString(authRaw, 'token'),
        username: pickString(authRaw, 'username'),
        password: pickString(authRaw, 'password'),
        token_url: pickString(authRaw, 'token_url', 'tokenUrl'),
        client_id: pickString(authRaw, 'client_id', 'clientId'),
        client_secret: pickString(authRaw, 'client_secret', 'clientSecret'),
        scope: pickString(authRaw, 'scope'),
        headers,
      }
    }
  }

  return {
    type,
    key: pickString(authRaw, 'key'),
    value: pickString(authRaw, 'value'),
    token: pickString(authRaw, 'token'),
    username: pickString(authRaw, 'username'),
    password: pickString(authRaw, 'password'),
    token_url: pickString(authRaw, 'token_url', 'tokenUrl'),
    client_id: pickString(authRaw, 'client_id', 'clientId'),
    client_secret: pickString(authRaw, 'client_secret', 'clientSecret'),
    scope: pickString(authRaw, 'scope'),
    headers: normalizeAwsJsonMap(authRaw.headers),
  }
}

export function normalizeOnCallAction(raw, index = 0) {
  const actionId =
    pickString(raw, 'action_id', 'actionId', 'id') || `on-call-${index}`

  const configRaw = raw.config ?? raw.action_config ?? {}
  const authRaw = configRaw.auth ?? {}
  const webhookAuthRaw = configRaw.webhook_auth ?? configRaw.webhookAuth ?? {}

  return {
    id: actionId,
    action_id: actionId,
    action_name: pickString(raw, 'action_name', 'actionName', 'name'),
    action_description: pickString(
      raw,
      'action_description',
      'actionDescription',
      'description',
    ),
    action_type: resolveActionType(raw, configRaw),
    ask_on_start: pickBool(raw, 'ask_on_start', 'askOnStart'),
    is_active: raw.is_active != null ? Boolean(raw.is_active) : raw.isActive != null ? Boolean(raw.isActive) : true,
    // Present only on actions auto-provisioned from a platform action
    // template (see BMS's action-template catalog) -- these get a
    // restricted "configure credentials" flow instead of the full editor,
    // since their name/config/message content is locked platform-side.
    template_id: pickString(raw, 'template_id', 'templateId'),
    // Auto-provisioned at onboarding (Chat Callback / Transfer to Owner /
    // Verify Contact Us) or owned by a Context -- backend
    // rejects deleting these; OnCallActionsActionMenu reads these two flags
    // to grey the Delete option out instead of letting it hit that error.
    is_onboarding_default: pickBool(raw, 'is_onboarding_default', 'isOnboardingDefault'),
    created_via_context: pickBool(raw, 'created_via_context', 'createdViaContext'),
    // Which credential keys already have a value set -- never the values
    // themselves (some are secrets), just enough for the form to show
    // "already configured" instead of asking the admin to re-enter
    // everything just to change one field.
    configured_credential_keys: Object.keys(raw.credentials || {}),
    notifications: normalizeOnCallNotifications(raw.notifications),
    config: {
      url: pickString(configRaw, 'url'),
      method: pickString(configRaw, 'method') || 'GET',
      headers: normalizeAwsJsonMap(configRaw.headers),
      params: normalizeAwsJsonMap(configRaw.params),
      body: normalizeAwsJsonMap(configRaw.body),
      sample_values: normalizeAwsJsonMap(configRaw.sample_values ?? configRaw.sampleValues),
      auth: authFieldsFromRecord(authRaw),
      transfer_number: pickString(configRaw, 'transfer_number', 'transferNumber'),
      transfer_message: pickString(configRaw, 'transfer_message', 'transferMessage'),
      response_status_path: pickString(
        configRaw,
        'response_status_path',
        'responseStatusPath',
      ),
      response_status_success_value: pickString(
        configRaw,
        'response_status_success_value',
        'responseStatusSuccessValue',
      ),
      response_message_path: pickString(
        configRaw,
        'response_message_path',
        'responseMessagePath',
      ),
      success_message: pickString(configRaw, 'success_message', 'successMessage'),
      error_message: pickString(configRaw, 'error_message', 'errorMessage'),
      greeting: pickString(configRaw, 'greeting'),
      mission_prompt: pickString(configRaw, 'mission_prompt', 'missionPrompt'),
      required_fields: (() => {
        const parsed = parseAwsJson(configRaw.required_fields ?? configRaw.requiredFields)
        return Array.isArray(parsed) ? parsed : []
      })(),
      max_duration: configRaw.max_duration ?? configRaw.maxDuration ?? null,
      retry_attempts: configRaw.retry_attempts ?? configRaw.retryAttempts ?? null,
      retry_interval: configRaw.retry_interval ?? configRaw.retryInterval ?? null,
      webhook_url: pickString(configRaw, 'webhook_url', 'webhookUrl'),
      webhook_method: pickString(configRaw, 'webhook_method', 'webhookMethod') || 'POST',
      webhook_auth: authFieldsFromRecord(webhookAuthRaw),
      to_number:
        pickString(configRaw, 'to_number', 'toNumber')
        || (Array.isArray(configRaw.caller_list) && configRaw.caller_list[0]
          ? pickString(configRaw.caller_list[0], 'number')
          : ''),
    },
  }
}

export function normalizeOnCallActionsList(rows) {
  const list = Array.isArray(rows) ? rows : []
  return list.map((row, index) => normalizeOnCallAction(row, index))
}

export function formFromOnCallAction(record) {
  const base = defaultOnCallForm()
  if (!record) return base

  const config = record.config || {}
  const auth = config.auth || {}
  const webhookAuth = config.webhook_auth || {}

  return {
    action_name: record.action_name || '',
    action_description: record.action_description || '',
    action_type: resolveActionType(record, config),
    ask_on_start: Boolean(record.ask_on_start),
    is_active: record.is_active !== false,
    config: {
      ...base.config,
      url: config.url || '',
      method: config.method || 'GET',
      headers: stringifyJsonField(config.headers),
      params: stringifyJsonField(config.params),
      body: stringifyJsonField(config.body),
      sample_values: normalizeAwsJsonMap(config.sample_values),
      body_fields: bodyFieldsFromJson(
        resolveBodySourceForForm(config, auth),
        config.sample_values,
      ),
      auth: {
        ...defaultAuthConfig(),
        ...authFieldsFromRecord(auth),
        headers: stringifyJsonField(auth.headers),
        custom_headers:
          (auth.type || '') === 'custom_header' ? customHeadersFromAuth(auth) : [],
      },
      transfer_number: config.transfer_number || '',
      transfer_message: config.transfer_message || '',
      response_status_path: config.response_status_path || '',
      response_status_success_value: config.response_status_success_value || '',
      response_message_path: config.response_message_path || '',
      success_message: config.success_message || '',
      error_message: config.error_message || '',
      greeting: config.greeting || '',
      mission_prompt: config.mission_prompt || '',
      required_field_rows: requiredFieldsFromAwsJson(config.required_fields),
      max_duration: config.max_duration ?? 180,
      retry_attempts: config.retry_attempts ?? 2,
      retry_interval: config.retry_interval ?? 300,
      webhook_url: config.webhook_url || '',
      webhook_method: config.webhook_method || 'POST',
      webhook_auth: {
        ...defaultAuthConfig(),
        ...authFieldsFromRecord(webhookAuth),
        headers: stringifyJsonField(webhookAuth.headers),
        custom_headers:
          (webhookAuth.type || '') === 'custom_header'
            ? customHeadersFromAuth(webhookAuth)
            : [],
      },
      to_number: config.to_number || '',
    },
    notifications: record.notifications
      ? buildNotificationsForm(record)
      : defaultNotificationsForm(),
    test_params: '{}',
  }
}

function resolveBodySourceForForm(config, auth) {
  const body = normalizeAwsJsonMap(config.body)
  if (Object.keys(body).length) return config.body

  if ((auth?.type || '') === 'custom_header') {
    const params = normalizeAwsJsonMap(config.params)
    if (Object.keys(params).length) return config.params
  }

  return config.body
}

function buildConfigInput(form) {
  const config = form.config || {}
  const auth = config.auth || {}
  const actionType = form.action_type

  const payload = {
    response_status_path: config.response_status_path || undefined,
    response_status_success_value: config.response_status_success_value || undefined,
    success_message: config.success_message || undefined,
    error_message: config.error_message || undefined,
  }

  if (actionType === 'CALL_REDIRECT') {
    payload.transfer_number = config.transfer_number || undefined
    payload.transfer_message = config.transfer_message || undefined
    return payload
  }

  if (actionType === 'OUTGOING_CALL') {
    payload.greeting = config.greeting || undefined
    payload.mission_prompt = config.mission_prompt || undefined

    const requiredFields = requiredFieldsToAwsJson(config.required_field_rows)
    if (requiredFields) payload.required_fields = requiredFields

    const toNumber = String(config.to_number || '').trim()
    if (toNumber) {
      payload.caller_list = [{ number: toNumber }]
    }

    payload.max_duration = parsePositiveInt(config.max_duration, 180)
    payload.retry_attempts = parsePositiveInt(config.retry_attempts, 2)
    payload.retry_interval = parsePositiveInt(config.retry_interval, 300)
    payload.webhook_url = config.webhook_url || undefined
    payload.webhook_method = config.webhook_method || 'POST'
    payload.webhook_auth = buildWebhookAuthInput(config.webhook_auth)
    return payload
  }

  payload.url = config.url || undefined
  payload.method = config.method || undefined

  const method = String(config.method || 'GET').toUpperCase()
  const isCustomHeaderAuth = auth.type === 'custom_header'
  const useParamsForFieldPayload = isCustomHeaderAuth && method === 'GET'

  const requestHeaders = prepareAwsJsonInput(config.headers, { requireObject: true })
  const requestParams = prepareAwsJsonInput(config.params, { requireObject: true })
  const requestBody = bodyJsonFromFields(config.body_fields)
    || prepareAwsJsonInput(config.body, { requireObject: false })

  if (requestHeaders) payload.headers = requestHeaders

  if (useParamsForFieldPayload) {
    const fieldPayload = requestBody || requestParams
    if (fieldPayload) payload.params = fieldPayload
  } else {
    if (requestParams) payload.params = requestParams
    if (requestBody) payload.body = requestBody
  }

  const sampleValuesJson = sampleValuesJsonFromFields(
    config.body_fields,
    config.sample_values,
    form.action_description,
  )
  if (sampleValuesJson) payload.sample_values = sampleValuesJson

  if (auth.type && auth.type !== 'none') {
    payload.auth = buildGraphqlAuthInput(auth)
  }

  return payload
}

export const CREATE_ON_CALL_ACTION_FIELDS = [
  'action_name',
  'action_type',
  'action_description',
  'ask_on_start',
  'is_active',
  'config',
]

export const UPDATE_ON_CALL_ACTION_FIELDS = [
  'action_id',
  'action_name',
  'action_description',
  'action_type',
  'ask_on_start',
  'is_active',
  'config',
  'notifications',
]

export const ON_CALL_ACTION_CONFIG_FIELDS = [
  'url',
  'method',
  'headers',
  'params',
  'body',
  'auth',
  'transfer_number',
  'transfer_message',
  'response_status_path',
  'response_status_success_value',
  'success_message',
  'error_message',
  'sample_values',
  'greeting',
  'mission_prompt',
  'required_fields',
  'max_duration',
  'retry_attempts',
  'retry_interval',
  'webhook_url',
  'webhook_method',
  'webhook_auth',
  'caller_list',
]

export const ON_CALL_AUTH_CONFIG_FIELDS = ON_CALL_AUTH_GRAPHQL_FIELDS

export const TEST_ON_CALL_ACTION_FIELDS = ['action_id', 'config', 'to_number']

function stripUndefined(value) {
  if (value === undefined) return undefined
  if (value == null || typeof value !== 'object') return value
  if (Array.isArray(value)) {
    return value.map((item) => stripUndefined(item)).filter((item) => item !== undefined)
  }

  const next = {}
  Object.entries(value).forEach(([key, entry]) => {
    if (entry === undefined) return
    const normalized = stripUndefined(entry)
    if (normalized !== undefined) next[key] = normalized
  })
  return next
}

function pickAllowedFields(source, allowedFields) {
  if (!source || typeof source !== 'object') return undefined

  const allowed = new Set(allowedFields)
  const picked = {}

  Object.entries(source).forEach(([key, value]) => {
    if (!allowed.has(key) || value === undefined) return
    picked[key] = value
  })

  const cleaned = stripUndefined(picked)
  return cleaned && Object.keys(cleaned).length ? cleaned : undefined
}

export function sanitizeOnCallAuthConfigInput(auth) {
  return pickAllowedFields(buildGraphqlAuthInput(auth || {}), ON_CALL_AUTH_GRAPHQL_FIELDS)
}

const ON_CALL_NOTIFICATION_EMAIL_FIELDS = ['enabled', 'send_to', 'subject', 'fields', 'template']
const ON_CALL_NOTIFICATION_WHATSAPP_FIELDS = ['enabled', 'send_to', 'fields', 'template']
const ON_CALL_NOTIFICATION_WEBHOOK_FIELDS = ['enabled', 'url', 'fields', 'auth']

function sanitizeOnCallNotificationsInput(notifications) {
  if (!notifications || typeof notifications !== 'object') return notifications

  const next = {}

  if (notifications.email) {
    const email = pickAllowedFields(notifications.email, ON_CALL_NOTIFICATION_EMAIL_FIELDS)
    if (email) next.email = email
  }

  if (notifications.whatsapp) {
    const whatsapp = pickAllowedFields(notifications.whatsapp, ON_CALL_NOTIFICATION_WHATSAPP_FIELDS)
    if (whatsapp) next.whatsapp = whatsapp
  }

  if (notifications.webhook) {
    const webhook = pickAllowedFields(notifications.webhook, ON_CALL_NOTIFICATION_WEBHOOK_FIELDS)
    if (webhook) {
      if (webhook.auth) {
        webhook.auth = sanitizeOnCallAuthConfigInput(webhook.auth) || { type: 'none' }
      }
      next.webhook = webhook
    }
  }

  return Object.keys(next).length ? next : notifications
}

export function sanitizeOnCallActionConfigInput(config) {
  const picked = pickAllowedFields(config, ON_CALL_ACTION_CONFIG_FIELDS)
  if (!picked) return undefined

  if (picked.auth) {
    picked.auth = sanitizeOnCallAuthConfigInput(picked.auth)
    if (!picked.auth) delete picked.auth
  }

  if (picked.webhook_auth) {
    picked.webhook_auth = sanitizeOnCallAuthConfigInput(picked.webhook_auth)
    if (!picked.webhook_auth) delete picked.webhook_auth
  }

  return Object.keys(picked).length ? picked : undefined
}

export function sanitizeCreateOnCallActionInput(input) {
  const picked = pickAllowedFields(input, CREATE_ON_CALL_ACTION_FIELDS)
  if (!picked) return {}

  if (picked.config) {
    picked.config = sanitizeOnCallActionConfigInput(picked.config)
    if (!picked.config) delete picked.config
  }

  if (picked.notifications) {
    picked.notifications = sanitizeOnCallNotificationsInput(picked.notifications)
  }

  return picked
}

export function sanitizeUpdateOnCallActionInput(input) {
  const picked = pickAllowedFields(input, UPDATE_ON_CALL_ACTION_FIELDS)
  if (!picked) return {}

  if (picked.config) {
    picked.config = sanitizeOnCallActionConfigInput(picked.config)
    if (!picked.config) delete picked.config
  }

  if (picked.notifications) {
    picked.notifications = sanitizeOnCallNotificationsInput(picked.notifications)
  }

  return picked
}

export function sanitizeTestOnCallActionInput(input) {
  const picked = pickAllowedFields(input, TEST_ON_CALL_ACTION_FIELDS)
  if (!picked) return {}

  if (picked.config) {
    picked.config = sanitizeOnCallActionConfigInput(picked.config)
    if (!picked.config) delete picked.config
  }

  if (picked.notifications) {
    picked.notifications = sanitizeOnCallNotificationsInput(picked.notifications)
  }

  return picked
}

export function buildUpdateOnCallMappingsInput(actionId, mappings = {}, record = null) {
  const form = applySuggestionsToForm(formFromOnCallAction(record), mappings)

  return sanitizeUpdateOnCallActionInput({
    action_id: String(actionId ?? '').trim(),
    config: buildConfigInput(form),
  })
}

export function buildUpdateOnCallAfterTestInput(
  actionId,
  mappings = {},
  record = null,
  deliveryConfig = null,
) {
  const form = applySuggestionsToForm(formFromOnCallAction(record), mappings)

  if (deliveryConfig) {
    form.config.webhook_url = deliveryConfig.webhook_url || ''
    form.config.webhook_method = deliveryConfig.webhook_method || 'POST'
    form.config.webhook_auth = {
      ...defaultAuthConfig(),
      ...(deliveryConfig.webhook_auth || {}),
    }
  }

  return sanitizeUpdateOnCallActionInput({
    action_id: String(actionId ?? '').trim(),
    config: buildConfigInput(form),
  })
}

export function resolveOnCallActionId(response) {
  const payload = unwrapApiPayload(response) ?? response
  if (!payload || typeof payload !== 'object') return ''

  return (
    pickString(payload, 'action_id', 'actionId', 'id') ||
    pickString(payload.data, 'action_id', 'actionId', 'id') ||
    pickString(payload.items, 'action_id', 'actionId', 'id') ||
    ''
  )
}

export function buildCreateOnCallInput(form) {
  return sanitizeCreateOnCallActionInput({
    action_name: String(form.action_name || '').trim(),
    action_description: String(form.action_description || '').trim() || undefined,
    action_type: form.action_type,
    ask_on_start: Boolean(form.ask_on_start),
    is_active: form.is_active !== false,
    config: buildConfigInput(form),
  })
}

export function buildUpdateOnCallInput(form, actionId) {
  return sanitizeUpdateOnCallActionInput({
    action_id: actionId,
    action_name: String(form.action_name || '').trim() || undefined,
    action_description: String(form.action_description || '').trim() || undefined,
    action_type: form.action_type || undefined,
    ask_on_start: Boolean(form.ask_on_start),
    config: buildConfigInput(form),
  })
}

/** Update email template and/or notification send_to while preserving existing config. */
export function buildUpdateOnCallTemplateInput(record, {
  emailTemplate,
  emailSendToList,
  whatsappSendToList,
} = {}) {
  const actionId = String(record?.action_id || record?.id || '').trim()
  const form = formFromOnCallAction(record)
  const arraySendTo = usesArraySendTo(record?.action_type)
  const notificationsForm = buildNotificationsForm(record)

  if (emailTemplate !== undefined) {
    notificationsForm.email.template = String(emailTemplate || '').trim()
  }

  if (emailSendToList !== undefined) {
    notificationsForm.email.send_to_list = normalizeSendToList(emailSendToList)
    notificationsForm.email.send_to_mode = 'custom'
  }

  if (whatsappSendToList !== undefined) {
    notificationsForm.whatsapp.send_to_list = normalizeSendToList(whatsappSendToList)
  }

  return sanitizeUpdateOnCallActionInput({
    action_id: actionId,
    config: buildConfigInput(form),
    notifications: buildNotificationsInput(notificationsForm, { arraySendTo }),
  })
}

export function buildTestOnCallInput(form, actionId, testParamsSource = '{}', options = {}) {
  const resolvedActionId = String(actionId ?? '').trim()

  if (isOutgoingCallActionType(form?.action_type)) {
    const toNumber = String(options.to_number ?? '').trim()
    return sanitizeTestOnCallActionInput({
      action_id: resolvedActionId,
      to_number: toNumber || undefined,
    })
  }

  if (form?.action_type === 'CALL_REDIRECT') {
    const redirectConfig = buildConfigInput(form) || {}
    return sanitizeTestOnCallActionInput({
      action_id: resolvedActionId,
      config: {
        transfer_number: redirectConfig.transfer_number,
        transfer_message: redirectConfig.transfer_message,
      },
    })
  }

  const config = buildConfigInput(form) || {}

  const parsed = parseJsonField(testParamsSource, {})
  const params =
    parsed && typeof parsed === 'object' && !parsed.__invalid ? { ...parsed } : {}

  delete params.__invalid
  delete params.org_id
  delete params.action_id

  const allowedKeys = [
    'caller_number',
    'caller_name',
    'caller_email',
    'extracted_date',
    'extracted_time',
  ]

  const testParamsObj = {}
  allowedKeys.forEach((key) => {
    const value = params[key]
    if (value != null && String(value).trim()) {
      testParamsObj[key] = String(value).trim()
    }
  })

  if (Object.keys(testParamsObj).length) {
    const existingParams = parseJsonField(form?.config?.params, {})
    const mergedParams =
      existingParams && typeof existingParams === 'object' && !existingParams.__invalid
        ? { ...existingParams, ...testParamsObj }
        : { ...testParamsObj }

    const mergedParamsJson = prepareAwsJsonInput(mergedParams, { requireObject: true })
    if (mergedParamsJson) {
      config.params = mergedParamsJson
    }
  }

  return sanitizeTestOnCallActionInput({
    action_id: resolvedActionId,
    config,
  })
}

export function applySuggestionsToForm(form, suggestions = {}) {
  if (!form?.config || !suggestions) return form

  return {
    ...form,
    config: {
      ...form.config,
      response_status_path:
        suggestions.response_status_path || form.config.response_status_path,
      response_status_success_value:
        suggestions.response_status_success_value ||
        form.config.response_status_success_value,
      response_message_path:
        suggestions.response_message_path || form.config.response_message_path,
      success_message: suggestions.success_message || form.config.success_message,
      error_message: suggestions.error_message || form.config.error_message,
    },
  }
}

export function actionTypeLabel(value) {
  return ON_CALL_ACTION_TYPES.find((row) => row.value === value)?.title || value
}

export function actionTypeIcon(value) {
  return ON_CALL_ACTION_TYPES.find((row) => row.value === value)?.icon || 'mdi-lightning-bolt-outline'
}

export function actionAnnounceCaption(askOnStart) {
  return askOnStart ? 'Mention in the greeting' : 'Do not mention in greeting'
}

export function countActionsByType(actions = []) {
  const counts = {}
  ON_CALL_ACTION_TYPES.forEach((row) => {
    counts[row.value] = 0
  })
  actions.forEach((action) => {
    const type = action?.action_type
    if (type && Object.prototype.hasOwnProperty.call(counts, type)) {
      counts[type] += 1
    }
  })
  return counts
}

export function filterActionsByType(actions = [], typeFilter = 'ALL') {
  if (!typeFilter || typeFilter === 'ALL') return actions
  return actions.filter((action) => action?.action_type === typeFilter)
}
